import {
  format,
  startOfHour,
  endOfHour,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
} from 'date-fns';

export const WEEK_OPTIONS = { weekStartsOn: 1 as const }; // Monday

export type Gap =
  | '+1HOUR'
  | '+1DAY'
  | '+1WEEK'
  | '+1MONTH'
  | '+1QUARTER'
  | '+1YEAR';

export const getPeriodBounds = (dateValue: Date | number, gap: Gap) => {
  const d = new Date(dateValue);

  switch (gap) {
    case '+1HOUR':
      return { startDate: startOfHour(d), endDate: endOfHour(d) };

    case '+1DAY':
      return { startDate: startOfDay(d), endDate: endOfDay(d) };

    case '+1WEEK':
      return {
        startDate: startOfWeek(d, WEEK_OPTIONS),
        endDate: endOfWeek(d, WEEK_OPTIONS),
      };

    case '+1MONTH':
      return { startDate: startOfMonth(d), endDate: endOfMonth(d) };
    case '+1QUARTER':
      return { startDate: startOfQuarter(d), endDate: endOfQuarter(d) };

    case '+1YEAR':
      return { startDate: startOfYear(d), endDate: endOfYear(d) };

    default:
      // Fallback to day if something unexpected comes in
      return { startDate: startOfDay(d), endDate: endOfDay(d) };
  }
};
export const toUnix = (date: Date): number => Math.floor(date.getTime() / 1000);
export const fromUnix = (unix: number): Date => new Date(unix * 1000);
export function logInfoWithTime(...args) {
  const time = new Date().toLocaleString('en-GB', { timeZoneName: 'short' });
  console.log(`[${time}]`, ...args);
}

export function logErrorWithTime(...args) {
  const time = new Date().toLocaleString('en-GB', { timeZoneName: 'short' });
  console.error(`[${time}]`, ...args);
}

/** Milliseconds of one minute, hour and day, for the reset-time wording. */
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** `2h 10m`, `35m` or `3d 4h`: the two largest units of a span, minutes at least. */
const spanText = (ms: number): string => {
  const abs = Math.abs(ms);
  const days = Math.floor(abs / DAY_MS);
  const hours = Math.floor((abs % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((abs % HOUR_MS) / MINUTE_MS);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * A limit window's reset time as the Agent Sessions grid words it (SEN-5783): `in 2h 10m` while the reset is
 * less than a day ahead, the weekday and time (`Sat 12:00`, the browser's local time) when further, and
 * `reset 35m ago` (`reset Sat 12:00` beyond a day) once it has passed. `resetsAt` is a unix time in seconds.
 */
export const formatLimitReset = (
  resetsAt: number,
  nowMs: number = Date.now(),
): string => {
  const resetMs = resetsAt * 1000;
  const delta = resetMs - nowMs;
  if (delta >= 0) {
    return delta < DAY_MS
      ? `in ${spanText(delta)}`
      : format(resetMs, 'EEE HH:mm');
  }
  return -delta < DAY_MS
    ? `reset ${spanText(delta)} ago`
    : `reset ${format(resetMs, 'EEE HH:mm')}`;
};
